import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ClipboardList, Play, BarChart3, Info, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import simulationService from '@/services/simulationService.ts';
import type { SimulationResponse } from '@/types/simulation.ts';
import type { Maestro } from '@/types/maestro.ts';
import { maestros, getMaestroName, getMaestroTitle, getMaestroSubtitle, getMaestroDescription } from '@/types/maestro.ts';
import { useCatalogLabel } from '@/hooks/useCatalog.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import QuickPracticeModal from '@/components/simulation/QuickPracticeModal.tsx';

interface MaestroSimulationsProps {
  maestro: Maestro;
}

export default function MaestroSimulations({ maestro }: MaestroSimulationsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSalesManager } = useAuth();
  const { data: simulations = [], loading, error } = useAsyncData<SimulationResponse[]>(
    () => (isSalesManager ? simulationService.getAll() : simulationService.getMyAssignments()),
    [isSalesManager],
    { initial: [], fallbackError: t('simulation.loadError') },
  );
  const [quickPracticeOpen, setQuickPracticeOpen] = useState(false);

  const handleStart = (sim: SimulationResponse) => {
    navigate(`/simulation/${sim.id}`, { state: { maestroId: maestro.id } });
  };

  return (
    <div className="animate-fade-in">
      {/* Maestro header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{maestro.icon}</span>
          <div>
            <h1
              className="text-2xl font-heading"
              style={{ color: maestro.accentColor }}
            >
              {getMaestroName(maestro)}
            </h1>
            <p className="text-sm text-muted-foreground">
              {getMaestroTitle(maestro)} — {getMaestroSubtitle(maestro)}
            </p>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {getMaestroDescription(maestro)}
        </p>
      </div>

      {/* Maestro prompt */}
      <div
        className="mb-6 flex items-center justify-between gap-4 rounded-xl border p-4"
        style={{ borderColor: `${maestro.accentColor}30`, backgroundColor: `${maestro.accentColor}08` }}
      >
        <p className="text-sm" style={{ color: maestro.accentColor }}>
          {t('simulation.chooseMaestroPrompt', { name: getMaestroName(maestro) })}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setQuickPracticeOpen(true)}
          style={{ borderColor: `${maestro.accentColor}40`, color: maestro.accentColor }}
        >
          <Zap className="size-3.5" />
          {t('simulation.quickPractice')}
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-3 text-sm">{t('simulation.loadingSimulations')}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="mt-3 text-sm text-red-500">{error}</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && simulations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <ClipboardList className="h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">
            {isSalesManager ? t('simulation.noSimulationsManager') : t('simulation.noSimulationsSalesperson')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {isSalesManager
              ? t('simulation.noSimulationsManagerHint')
              : t('simulation.noSimulationsSalespersonAlt')}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setQuickPracticeOpen(true)}
            className="mt-4"
            style={{ borderColor: `${maestro.accentColor}40`, color: maestro.accentColor }}
          >
            <Zap className="size-4" />
            {t('simulation.quickPracticeWith', { name: getMaestroName(maestro) })}
          </Button>
        </div>
      )}

      {/* Simulation cards */}
      {!loading && !error && simulations.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {simulations.map((sim) => (
            <SimCard
              key={sim.id}
              simulation={sim}
              maestro={maestro}
              onStart={() => handleStart(sim)}
            />
          ))}
        </div>
      )}

      <QuickPracticeModal
        isOpen={quickPracticeOpen}
        onClose={() => setQuickPracticeOpen(false)}
        maestroId={maestro.id}
      />
    </div>
  );
}

function SimCard({
  simulation,
  maestro,
  onStart,
}: {
  simulation: SimulationResponse;
  maestro: Maestro;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  const typeLabel = useCatalogLabel('SimulationType', simulation.simulationType);
  const diffLabel = useCatalogLabel('Difficulty', String(simulation.difficultyLevel));
  const phaseLabel = useCatalogLabel('SalePhase', simulation.salePhase);

  return (
    <button
      onClick={onStart}
      className="group flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left transition-all hover:shadow-md"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="font-semibold text-foreground transition-colors">
          {simulation.name}
        </h3>
        <span
          className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: `${maestro.accentColor}15`, color: maestro.accentColor }}
        >
          {typeLabel}
        </span>
      </div>

      <p className="mb-3 text-sm text-muted-foreground">{phaseLabel}</p>

      {/* Forced maestro warning */}
      {simulation.assignedMaestroId && simulation.assignedMaestroId !== maestro.id && (() => {
        const forced = maestros.find((x) => x.id === simulation.assignedMaestroId);
        return forced ? (
          <div
            className="mb-3 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs"
            style={{ backgroundColor: `${forced.accentColor}10`, color: forced.accentColor }}
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>{t('simulation.forcedMaestro', { name: getMaestroName(forced) })}</span>
          </div>
        ) : null;
      })()}

      {/* Maestro badge (when matches current) */}
      {simulation.assignedMaestroId && simulation.assignedMaestroId === maestro.id && (
        <div
          className="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
          style={{ backgroundColor: `${maestro.accentColor}15`, color: maestro.accentColor }}
        >
          <span>{maestro.icon}</span>
          <span>{t('simulation.assignedMaestro')}</span>
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5" title={diffLabel}>
          <BarChart3 className="h-3.5 w-3.5" />
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  backgroundColor: level <= simulation.difficultyLevel
                    ? maestro.accentColor
                    : 'var(--border)',
                }}
              />
            ))}
          </div>
          <span>{diffLabel}</span>
        </div>

        <div
          className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100"
          style={{ color: maestro.accentColor }}
        >
          <Play className="h-3.5 w-3.5" />
          <span className="font-semibold">{t('common.practice')}</span>
        </div>
      </div>
    </button>
  );
}
